/**
 * Auth Context — Firebase Auth + Firestore profil yönetimi.
 *
 * 4 farklı kullanıcı tipini destekler:
 *  - BOSS / Sorumlu / Muhasebe: Email+Password ile giriş, kullanicilar/{auth.uid}
 *  - Personel: Anonymous Auth + custom claim 'personelId', kullanicilar/{personelId}
 *
 * Otomatik claim kontrolü: BOSS/Sorumlu/Muhasebe login olunca token'da `rol` claim'i
 * yoksa `bossClaimSetle` Cloud Function çağrılır (Storage rules için gerekli).
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { getFirebaseAuth, getFirestoreDb, getFirebaseFunctions } from '@/lib/firebase'
import type { Kullanici, Rol } from '@/types'

interface AuthContextType {
  firebaseUser: FirebaseUser | null
  kullanici: Kullanici | null
  rol: Rol | null
  yukleniyor: boolean
  girisYap: (email: string, sifre: string) => Promise<void>
  cikisYap: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [kullanici, setKullanici] = useState<Kullanici | null>(null)
  const [yukleniyor, setYukleniyor] = useState(true)

  useEffect(() => {
    const auth = getFirebaseAuth()

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user)

      if (user) {
        try {
          const isAnonymous = user.isAnonymous
          // Önce CACHE'li token — ağ gerektirmez, offline açılışta oturum düşmesin
          let tokenResult = await user.getIdTokenResult()

          const hasRolClaim = !!tokenResult.claims.rol
          const hasPersonelClaim = !!tokenResult.claims.personelId

          // Yönetici/BOSS: rol claim yoksa Cloud Function ile set et (yalnız online)
          if (!isAnonymous && !hasRolClaim) {
            console.log('🔄 Rol claim yok, Cloud Function çağrılıyor...')
            try {
              const fn = httpsCallable(getFirebaseFunctions(), 'bossClaimSetle')
              await fn()
              // Token'ı zorla yenile
              tokenResult = await user.getIdTokenResult(true)
              console.log('✅ Claim set edildi:', tokenResult.claims)
            } catch (err) {
              console.error('Claim set hatası:', err)
            }
          }

          // Personel: personelId claim cache'te yoksa BİR KEZ yenilemeyi dene;
          // başarısız olsa bile (offline) oturumu DÜŞÜRME — cache ile devam.
          if (isAnonymous && !hasPersonelClaim) {
            try {
              tokenResult = await user.getIdTokenResult(true)
            } catch (err) {
              console.warn('Personel token yenilenemedi (offline?), cache ile devam:', err)
            }
          }

          const claimedPersonelId = tokenResult.claims.personelId as string | undefined
          const docId = claimedPersonelId ?? user.uid

          const db = getFirestoreDb()
          const userDoc = await getDoc(doc(db, 'kullanicilar', docId))

          if (userDoc.exists()) {
            const data = userDoc.data()
            setKullanici({ id: docId, ...data } as Kullanici)
          } else {
            console.warn('Kullanıcı profili bulunamadı:', docId)
            setKullanici(null)
          }
        } catch (error) {
          console.error('Profil okuma hatası:', error)
          setKullanici(null)
        }
      } else {
        setKullanici(null)
      }

      setYukleniyor(false)
    })

    return () => unsubscribe()
  }, [])

  const girisYap = async (email: string, sifre: string) => {
    const auth = getFirebaseAuth()
    await signInWithEmailAndPassword(auth, email, sifre)
  }

  const cikisYap = async () => {
    const auth = getFirebaseAuth()
    await firebaseSignOut(auth)
  }

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        kullanici,
        rol: kullanici?.rol ?? null,
        yukleniyor,
        girisYap,
        cikisYap,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
